import { SwapOutlined } from "@ant-design/icons";
import { Space, DatePicker, Spin } from "antd";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { get, post } from "../Libraries/Ajax/HttpService";
import { toast } from "react-toastify";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Legend,
  Tooltip,
  Line,
  Label,
  Dot,
  ResponsiveContainer,
} from "recharts";

import Main from "../components/layout/Main";
import Header from "../components/layout/Header";
import back from "../assets/images/back.svg";
import { useNavigate } from "react-router-dom";
import { useDistrict } from "../utils/DistrictContext";

function Anomalynew() {
  const { RangePicker } = DatePicker;

  const [categories, setCategories] = useState([]);
  const [meterIds, setMeterIds] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMeters, setSelectedMeters] = useState([]);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(30, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [isApiDateApplied, setIsApiDateApplied] = useState(false);
  const [isClearHovered, setIsClearHovered] = useState(false);
  const [rangeValue, setRangeValue] = useState([
    dayjs().subtract(30, "days"),
    dayjs(),
  ]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [datastatusanomaly, setDatastatusanomaly] = useState(false);
  const [datastatusaccontability, setDatastatusaccountability] =
    useState(false);

  const { selectedDistrict } = useDistrict();
  const navigate = useNavigate();

  const onRedirection = () => navigate(-1);
  const LINE_COLORS = ["#4285F4", "#4a1ee9ff", "#00C853", "#FF9800"];
  const [anomalyMeterCount, setAnomalyMeterCount] = useState(0);
  const daysDifference = dayjs(endDate).diff(dayjs(startDate), "day") + 1;

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await get("apis/anoamaly_detection", {
          params: { category: null },
        });
        const cats = res?.data || [];
        setCategories(cats);

        // set first category as selected if exists
        if (cats.length > 0) {
          const firstCategory = cats[0].class_display_name || cats[0];
          setSelectedCategory(firstCategory);
        }
      } catch (err) {
        console.error("Error fetching categories", err);
      }
    };
    fetchCategories();
  }, [selectedDistrict]);

  // Fetch meters whenever category changes
  // useEffect(() => {
  //   const fetchMeters = async () => {
  //     try {
  //       const res = await get("apis/anoamaly_detection", {
  //         params: {
  //           category: selectedCategory || null,
  //           start_date: startDate,
  //           end_date: endDate,
  //         },
  //       });
  //       const meters = res?.data?.data || [];
  //       console.log("meters",meters);
  //       setMeterIds(meters);

  //       // Auto-select first meter
  //       if (meters.length > 0) {
  //         const firstId = meters[0].meter_id ?? meters[0];
  //         setSelectedMeters([String(firstId)]);
  //       }
  //     } catch (err) {
  //       console.error("Error fetching meter IDs", err);
  //     }
  //   };

  //   if (selectedCategory) fetchMeters();
  // }, [selectedCategory, startDate, endDate]);
  useEffect(() => {
    const fetchMeters = async () => {
      try {
        const res = await get("apis/anoamaly_detection", {
          params: {
            category: selectedCategory || null,
            start_date: startDate,
            end_date: endDate,
          },
        });

        // 🔹 meters
        const meters = res?.data?.data || [];
        setMeterIds(meters);
        setAnomalyMeterCount(meters.length);

        // 🔹 API date range
        const apiStart = res?.data?.start_date;
        const apiEnd = res?.data?.end_date;

        if (apiStart && apiEnd) {
          const parsedStart = dayjs(apiStart);
          const parsedEnd = dayjs(apiEnd);

          setStartDate(parsedStart.format("YYYY-MM-DD"));
          setEndDate(parsedEnd.format("YYYY-MM-DD"));
          setRangeValue([parsedStart, parsedEnd]);
        } else {
          // 🔹 fallback → last 30 days
          const fallbackStart = dayjs().subtract(30, "days");
          const fallbackEnd = dayjs();

          setStartDate(fallbackStart.format("YYYY-MM-DD"));
          setEndDate(fallbackEnd.format("YYYY-MM-DD"));
          setRangeValue([fallbackStart, fallbackEnd]);
        }

        // auto-select first meter
        if (meters.length > 0) {
          setSelectedMeters([String(meters[0].meter_id)]);
        }
        else if(meters.length==0){
          setSelectedMeters([]);
        }
      } catch (err) {
        console.error("Error fetching meter IDs", err);
      }
    };

    if (selectedCategory) fetchMeters();
  }, [selectedCategory, startDate, endDate]);

  // Fetch anomaly alert status
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("MCData"));
    if (!user?.userid) return;

    get(`apis/alert_status?user_id=${user.userid}`).then((res) => {
      const alert = res?.data?.data;
      setDatastatusanomaly(alert?.anomaly_alert_status);
    });
  }, []);

  useEffect(() => {
    if (!datastatusanomaly) return;

    const user = JSON.parse(localStorage.getItem("MCData"));
    if (!user?.userid) return;

    post(`apis/alert_status_change?user_id=${user.userid}&anomaly_seen=true`);
  }, [datastatusanomaly]);

  // Fetch anomaly data when date range or selected meters change
  useEffect(() => {
    if (!startDate || !endDate || selectedMeters.length === 0 || anomalyMeterCount===0) {
      setData([]);
      
      return;
    }

    setLoading(true);

    get("apis/anoamaly_detection", {
      params: {
        start_date: startDate,
        end_date: endDate,
        category: selectedCategory || "",
        meter_id: selectedMeters.join(","),
      },
    })
      .then((response) => {
        const datasets = response.data || [];
        const merged = {};
        const anomalyMeterSet = new Set();

        datasets.forEach((meterObj) => {
          const meterId = String(meterObj.meter_id);
          (meterObj.data || []).forEach((row) => {
            const parsed = dayjs(row.day, "MMM DD, YYYY");
            const dayKey = parsed.format("YYYY-MM-DD");
            const dayShort = parsed.format("DD MMM");
            const dayFull = parsed.format("MMM DD, YYYY");

            if (!merged[dayKey]) {
              merged[dayKey] = { day_short: dayShort, day_full: dayFull };
            }

            merged[dayKey][meterId] = Number(row.total_daily_flow);
            merged[dayKey][`anomaly_${meterId}`] = row.anomaly;
          });
        });

        const result = Object.keys(merged)
          .sort((a, b) => new Date(a) - new Date(b))
          .map((k) => merged[k]);

        setData(result);
      })
      .catch((err) => {
        console.error("Anomaly fetch error:", err);
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedCategory, selectedMeters]);

  const onDateRangeChange = (_, dateString) => {
    setStartDate(dateString[0]);
    setEndDate(dateString[1]);
  };

  const handleFlowPageclick = () => {
    navigate("/totalflow");
  };
 const handlePredictivePageclick = () => {
    console.log("Predictive Analytics");

    navigate("/predictiveanalysis");
  };
  const toggleMeterSelection = (meterId) => {
    setSelectedMeters((prev) => {
      const exists = prev.includes(meterId);

      if (exists) {
        return prev.filter((m) => m !== meterId);
      }

      if (prev.length >= 4) {
        toast.error(
          "Maximum 4 meters can be selected. Uncheck one to continue."
        );
        return prev;
      }

      return [...prev, meterId];
    });
  };

  // Tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const row = payload[0].payload;
    return (
      <div style={{ background: "#fff", padding: 8, borderRadius: 4 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{row.day_full}</div>
        {selectedMeters.length > 0 && (
          <div>
            {selectedMeters.map((meterId) => {
              const value = row[meterId];
              const isAnomaly = row[`anomaly_${meterId}`] === 1;
              const color =
                LINE_COLORS[selectedMeters.indexOf(meterId)] || "#666";
              return (
                <div
                  key={meterId}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    minWidth: 160,
                    marginBottom: 4,
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: isAnomaly ? "red" : color,
                      }}
                    />
                    <div style={{ fontSize: 13 }}>{`${meterId} :`}</div>
                  </div>
                  <div style={{ fontSize: 13 }}>
                    {value == null ? "-" : Number(value).toLocaleString()}
                    {isAnomaly ? " ⚠️" : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Header />
      <Main>
        <div className="waterDetails">
          <div className="d-flex justify-content-between mb-4">
            <div className="d-flex align-items-center">
              <img
                src={back}
                alt=""
                style={{ width: "10px", cursor: "pointer" }}
                onClick={onRedirection}
              />
              <div
                className="new-box-padding align-self-center c-pointer"
                onClick={handleFlowPageclick}
                style={{ borderBottom: "2px solid #ccc" }}
              >
                Flow Analytics
                {datastatusaccontability && (
                  <span className="ms-2" style={{ color: "red" }}>
                    !
                  </span>
                )}
              </div>
               <div className="" style={{ width: '2px', height: '25px', background: '#ccc' }}></div>
                                <div className="new-box-padding   align-self-center c-pointer"
                                    onClick={handlePredictivePageclick}
                                    style={{
                                        borderBottom: '2px solid #ccc',
                                    }}
                                >
                                    Predictive Analytics
                                </div>
              <div
                style={{ width: "2px", height: "25px", background: "#ccc" }}
              />
              <div
                className="new-box-padding text-blue c-pointer"
                style={{
                  borderBottom: "2px solid #1998f0",
                  marginLeft: "10px",
                }}
              >
                Anomaly Detection
                {datastatusanomaly && (
                  <span className="ms-2" style={{ color: "red" }}>
                    !
                  </span>
                )}
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              gap: "12px",
              width: "100%",
              marginTop: "12px",
              marginBottom: "12px",
            }}
          >
            {/* 1. Category Group */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  color: "#1998f0",
                  fontSize: "14px",
                  marginBottom: "5px",
                }}
              >
                Category
              </span>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setData([]);
                  setSelectedMeters([]);
                  setMeterIds([]);
                }}
                className="form-select"
                style={{
                  width: "240px",
                  height: "50px",
                  cursor: "pointer",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  boxShadow:
                    "0 10px 15px 0 rgb(207 217 230 / 30%), inset 0 11px 13px 0 rgb(230 236 242 / 40%)",
                }}
              >
                {categories?.map((cat) => {
                  const displayName = cat.class_display_name || cat;
                  const code = cat.mizuwatch_code || cat;
                  return (
                    <option key={code} value={displayName}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>

            {/* 2. Middle Text */}
            <div
              className="selectCalender"
              style={{
                height: "50px",
                marginBottom: "0px",
                fontSize: "16px",
                fontWeight: "500",
                color: "#444",
                whiteSpace: "nowrap",
              }}
            >
              Meters with Anomalies During
            </div>

            {/* 3. Date Range Group */}
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                style={{
                  color: "#1998f0",
                  fontSize: "14px",
                  marginBottom: "5px",
                }}
              >
                Date Range
              </span>
              <div className="selectCalender">
                <Space size={1}>
                  {/* <RangePicker
                    defaultValue={[dayjs().subtract(30, "days"), dayjs()]}
                    format="YYYY-MM-DD"
                    onChange={onDateRangeChange}
                    separator={<SwapOutlined />}
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    style={{
                      width: "260px",
                      height: "38px",
                      cursor: "pointer",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                      boxShadow:
                        "0 10px 15px 0 rgb(207 217 230 / 30%), inset 0 11px 13px 0 rgb(230 236 242 / 40%)",
                    }}
                  /> */}
                  <RangePicker
                    value={rangeValue}
                    format="YYYY-MM-DD"
                    onChange={(dates, dateStrings) => {
                      if (!dates) return;

                      setRangeValue(dates);
                      setStartDate(dateStrings[0]);
                      setEndDate(dateStrings[1]);
                    }}
                    separator={<SwapOutlined />}
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    style={{
                      width: "260px",
                      height: "38px",
                      cursor: "pointer",
                      border: "1px solid #d9d9d9",
                      borderRadius: "6px",
                    }}
                  />
                </Space>
              </div>
            </div>
          </div>
          {/* Main content */}
          <div className="row">
            {/* Left sidebar */}
            <div className="col-12 col-lg-3">
              <div
                className="bg-white p-3"
                style={{
                  borderRadius: "16px",
                  height: "700px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <h5 className="mb-3">
                  {anomalyMeterCount > 1
                    ? `${anomalyMeterCount} anomaly meters detected during the selected ${daysDifference} days`
                    : `${anomalyMeterCount} anomaly meter detected during the selected ${daysDifference} days`}
                </h5>
                {selectedMeters.length > 0 && (
                  <div className="d-flex align-items-center mb-3">
                    <span
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        backgroundColor: "#d5eaf8ff", // Blue Badge
                        color: "#21a9e9ff",
                        height: "16px",
                        minWidth: "20px",
                        padding: "0 6px",
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: "600",
                        marginRight: "8px",
                      }}
                    >
                      {selectedMeters.length}
                    </span>

                    {selectedMeters.length > 0 && (
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMeters([]);
                        }}
                        onMouseEnter={() => setIsClearHovered(true)}
                        onMouseLeave={() => setIsClearHovered(false)}
                        style={{
                          color: "red",
                          fontSize: "13px",
                          cursor: "pointer",
                          fontWeight: "500",
                          textDecoration: isClearHovered ? "underline" : "none",
                          transition: "text-decoration 0.2s ease",
                        }}
                      >
                        Clear
                      </span>
                    )}
                  </div>
                )}
                <div
                  className="custom-scrollbar"
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: "8px",
                    minHeight: 0,
                  }}
                >
                  {meterIds.length === 0 ? (
                    <p className="text-muted">No Meters</p>
                  ) : (
                    meterIds?.map((m, i) => {
                      const id = m.meter_id ?? m;
                      const selected = selectedMeters.includes(String(id));
                      return (
                        <div
                          key={i}
                          onClick={() => toggleMeterSelection(String(id))}
                          className="p-3 mb-2 border rounded d-flex justify-content-between align-items-center"
                          style={{
                            cursor: "pointer",
                            background: selected ? "#e8f0fe" : "#fff",
                            border: selected
                              ? "1px solid #4285F4"
                              : "1px solid #dee2e6",
                          }}
                        >
                          <div className="d-flex flex-column text-start align-items-start">
                            <span
                              className="fw-bold text-dark"
                              style={{ fontSize: "16px" }}
                            >
                              {id}
                            </span>
                            <span
                              className="text-muted"
                              style={{ fontSize: "12px" }}
                            >
                              {selectedCategory}
                            </span>
                            <span
                              className="text-muted"
                              style={{ fontSize: "12px" }}
                            >
                              Date:{" "}
                              {m.highest_flow_time
                                ? dayjs(m.highest_flow_time).format(
                                    "MMM DD, YYYY"
                                  )
                                : "-"}
                            </span>
                          </div>

                          <div className="text-end">
                            <span
                              className="fw-bold text-dark"
                              style={{ fontSize: "16px" }}
                            >
                              {m.highest_flow
                                ? Number(m.highest_flow).toLocaleString()
                                : "-"}
                            </span>
                            <div style={{ fontSize: "11px", color: "#888" }}>
                              gallons
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Right chart */}
            <div className="col-12 col-lg-9">
              <div
                className="water-chartLists bg-white"
                style={{ borderRadius: "16px", minHeight: "700px" }}
              >
                <div className="col-12 pb-5">
                  <div className="pt-4">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spin size="large" />
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={500}>
                        <LineChart data={data}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="day_short" />
                          <YAxis>
                            <Label
                              value="Gallons"
                              position="insideLeft"
                              angle={-90}
                              style={{ textAnchor: "middle", fontSize: 16 }}
                            />
                          </YAxis>

                          <Tooltip content={<CustomTooltip />} />
                          <Legend />

                          {selectedMeters.map((meterId, index) => {
                            const color = LINE_COLORS[index] || LINE_COLORS[0];
                            return (
                              <Line
                                key={meterId}
                                type="monotone"
                                dataKey={meterId}
                                name={`${meterId}`}
                                stroke={color}
                                strokeWidth={3}
                                activeDot={{ r: 6 }}
                                dot={(props) => {
                                  const isAnomaly =
                                    props.payload?.[`anomaly_${meterId}`] === 1;
                                  return (
                                    <Dot
                                      {...props}
                                      r={isAnomaly ? 4 : 2}
                                      fill={isAnomaly ? "red" : color}
                                      stroke={isAnomaly ? "red" : color}
                                      strokeWidth={2}
                                    />
                                  );
                                }}
                              />
                            );
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Main>
    </>
  );
}

export default Anomalynew;
